// Folder: src/stores/
// File: appStore.ts

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '@/app-lib/api-client';
import { ScheduledVideo } from '@/types/scheduling';

// Types
interface Project {
  project_id:string
  name: string;
  status: string;
  created_at: string;
  updated_at?: string;
  prompt?: string;
  metadata?: {
    title?: string;
    description?: string;
    tags?: string[];
  };
  assets?: {
    thumbnail_url?: string;
    thumbnailUrl?:string;
    background_url?: string;
    audio_url?: string;
    video_url?: string;
    videoUrl?: string;
    baseImageUrl?:string;
    backgroundUrl?:string;
  };
  analytics?: {
    views?: number;
    likes?: number;
    comments?: number;
    watch_time?: number;
    estimated_revenue?: number;
  };
  priority?: 'low' | 'medium' | 'high';           // ADD THIS
  performance_score?: number;                     // ADD THIS
  youtubeUrl?: string;
  youtube_video_id?: string;
  channel_id?: string;
}

interface Channel {
  id: string;
  name: string;
  channel_name?: string;
  channel_id: string;
  thumbnail?: string;
  subscriber_count?: number;
  project_count?: number;
  last_activity?: string;
  youtube_authenticated?: boolean;
  scheduling_enabled?: boolean;
}

interface ScheduleSettings {
  user_id: string;
  channel_id: string;
  schedule_config: {
    videos_per_week: number;
    preferred_days: string[];
    preferred_times: string[];
    timezone: string;
    auto_generate: boolean;
  };
  content_config: {
    language: string;
    video_style: string;
    content_themes: string[];
    duration_preference: string;
    use_trending_topics: boolean;
  };
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

interface SubscriptionStatus {
  subscription: {
    user_id: string;
    tier: 'free' | 'pro' | 'enterprise';
    channels_limit: number;
    videos_per_month: number;
    auto_schedule_enabled: boolean;
  };
  usage: {
    channels_used: number;
    videos_this_month: number;
    active_schedules: number;
  };
  limits: {
    channels_limit: number;
    videos_per_month: number;
    auto_schedule_enabled: boolean;
  };
  can_create_channel: boolean;
  can_create_video: boolean;
}

interface TenantTheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
}

interface AppState {
  // UI State
  theme: 'light' | 'dark';
  sidebarCollapsed: boolean;
  currentTenant: string | null;
  tenantTheme: TenantTheme;
  
  setSelectedChannel: (channelId: string) => void;
  deleteScheduledVideo: (channelId: string, scheduleId: string) => Promise<void>;
  updateScheduledVideoStatus: (channelId: string, scheduleId: string, status: string) => Promise<void>;
  createScheduleSettings: (channelId: string, settings: any) => Promise<void>;

  // Data State
  projects: Project[];
  channels: Channel[];
  selectedChannel: string | null; // Changed to string | null to match usage
  scheduleSettings: Record<string, ScheduleSettings>;
  scheduledVideos: ScheduledVideo[];
  notifications: Notification[];
  subscriptionStatus: SubscriptionStatus | null;
  
  // Loading States
  loading: {
    projects: boolean;
    channels: boolean;
    scheduling: boolean;
    subscription: boolean;
  };
  
  // Error States
  errors: {
    projects: string | null;
    channels: string | null;
    scheduling: string | null;
    subscription: string | null;
  };
  
  // Actions
  toggleTheme: () => void;
  toggleSidebar: () => void;
  setCurrentTenant: (tenant: string, theme?: TenantTheme) => void;
  selectChannel: (channelId: string) => void;
  // setSelectedChannel: (channelId: string) => void;
  
  // Helper methods
  getSelectedChannelData: () => Channel | null;
  
  // Data Actions
  fetchProjects: () => Promise<void>;
  fetchChannels: () => Promise<void>;
  fetchScheduleSettings: (channelId: string) => Promise<void>;
  fetchScheduledVideos: (channelId: string) => Promise<void>;
  fetchSubscriptionStatus: () => Promise<void>;
  
  // Project Actions
  createProject: (data: { prompt: string; client_id: string; channel_id: string }) => Promise<Project>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  
  // Scheduling Actions
  // createScheduleSettings: (channelId: string, settings: any) => Promise<void>;
  updateScheduleSettings: (channelId: string, settings: any) => Promise<void>;
  generateSchedule: (channelId: string) => Promise<void>;
  
  // Notification Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  
  // Utility Actions
  clearErrors: () => void;
}

const DEFAULT_TENANT_THEME: TenantTheme = {
  primary: '#3b82f6',
  secondary: '#64748b',
  accent: '#06b6d4',
  background: '#ffffff',
  surface: '#f8fafc',
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial State
      theme: 'light',
      sidebarCollapsed: false,
      currentTenant: null,
      tenantTheme: DEFAULT_TENANT_THEME,
      
      projects: [],
      channels: [],
      selectedChannel: null,
      scheduleSettings: {},
      scheduledVideos: [],
      notifications: [],
      subscriptionStatus: null,
      
      loading: {
        projects: false,
        channels: false,
        scheduling: false,
        subscription: false,
      },
      
      errors: {
        projects: null,
        channels: null,
        scheduling: null,
        subscription: null,
      },
      
      // UI Actions
      toggleTheme: () => {
        const newTheme = get().theme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        
        // Update document class for Tailwind dark mode
        document.documentElement.classList.toggle('dark', newTheme === 'dark');
      },
      
      toggleSidebar: () => {
        set({ sidebarCollapsed: !get().sidebarCollapsed });
      },
      
      setCurrentTenant: (tenant: string, theme?: TenantTheme) => {
        const tenantTheme = theme || DEFAULT_TENANT_THEME;
        set({ currentTenant: tenant, tenantTheme });
        
        // Apply tenant theme to CSS variables
        const root = document.documentElement;
        root.style.setProperty('--tenant-primary', tenantTheme.primary);
        root.style.setProperty('--tenant-secondary', tenantTheme.secondary);
        root.style.setProperty('--tenant-accent', tenantTheme.accent);
        root.style.setProperty('--tenant-background', tenantTheme.background);
        root.style.setProperty('--tenant-surface', tenantTheme.surface);
      },
      
      selectChannel: (channelId: string) => {
        set({ selectedChannel: channelId });
      },
      
      setSelectedChannel: (channelId: string) => {
        set({ selectedChannel: channelId });
         // Fetch related data for the selected channel
        if (channelId) {
          const { fetchScheduleSettings, fetchScheduledVideos } = get();
          fetchScheduleSettings(channelId);
          fetchScheduledVideos(channelId);
        }
      },
      
      // Helper method to get selected channel data
      getSelectedChannelData: () => {
        const state = get();
        if (!state.selectedChannel) return null;
        return state.channels.find(c => c.channel_id === state.selectedChannel) || null;
      },
      
      // Data Fetching Actions
      fetchProjects: async () => {
        try {
          set({ loading: { ...get().loading, projects: true }, errors: { ...get().errors, projects: null } });
          
          const response = await apiClient.get('/api/projects');
          set({ projects: response.data });
        } catch (error: any) {
          console.error('Fetch projects error:', error);
          set({ errors: { ...get().errors, projects: error.message } });
          
          get().addNotification({
            type: 'error',
            title: 'Failed to load projects',
            message: 'There was an error loading your projects. Please try again.',
          });
        } finally {
          set({ loading: { ...get().loading, projects: false } });
        }
      },
      
      fetchChannels: async () => {
        try {
          set({ loading: { ...get().loading, channels: true }, errors: { ...get().errors, channels: null } });
          
          const response = await apiClient.get('/api/users/channels');
          
          // Handle different response formats
          let channelsData = [];
          if (Array.isArray(response.data)) {
            channelsData = response.data;
          } else if (response.data && Array.isArray(response.data.channels)) {
            channelsData = response.data.channels;
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            channelsData = response.data.data;
          }

          set({ channels: channelsData });
          
          // If no channel is selected but we have channels, don't auto-select
          // Let the user choose explicitly
          
        } catch (error: any) {
          console.error('Fetch channels error:', error);
          set({ errors: { ...get().errors, channels: error.message } });
          
          get().addNotification({
            type: 'error',
            title: 'Failed to load channels',
            message: 'There was an error loading your channels. Please try again.',
          });
        } finally {
          set({ loading: { ...get().loading, channels: false } });
        }
      },

      fetchScheduleSettings: async (channelId: string) => {
        try {
          set({ loading: { ...get().loading, scheduling: true }, errors: { ...get().errors, scheduling: null } });
          
          const response = await apiClient.get(`/api/scheduling/channels/${channelId}/settings`);
          set({ 
            scheduleSettings: { 
              ...get().scheduleSettings, 
              [channelId]: response.data 
            } 
          });
        } catch (error: any) {
          if (error.response?.status !== 404) {
            console.error('Fetch schedule settings error:', error);
            set({ errors: { ...get().errors, scheduling: error.message } });
          }
        } finally {
          set({ loading: { ...get().loading, scheduling: false } });
        }
      },
      
      fetchScheduledVideos: async (channelId: string) => {
        try {
          const response = await apiClient.get(`/api/scheduling/channels/${channelId}/scheduled-videos`);
          set({ scheduledVideos: response.data });
        } catch (error: any) {
          console.error('Fetch scheduled videos error:', error);
        }
      },
      
      fetchSubscriptionStatus: async () => {
        try {
          set({ loading: { ...get().loading, subscription: true }, errors: { ...get().errors, subscription: null } });
          
          // const response = await apiClient.get('/api/users/subscription-status');
           const response = await apiClient.get('/api/users/subscription-status', {
      timeout: 60000 // Increased from default 15s to 60s
    });
          set({ subscriptionStatus: response.data });
        } catch (error: any) {
          console.error('Fetch subscription status error:', error);
          set({ errors: { ...get().errors, subscription: error.message } });
        } finally {
          set({ loading: { ...get().loading, subscription: false } });
        }
      },
      
      // Project Actions
      createProject: async (data) => {
        try {
          const response = await apiClient.post('/api/projects', data);
          const newProject = response.data;
          
          set({ projects: [newProject, ...get().projects] });
          
          get().addNotification({
            type: 'success',
            title: 'Project Created',
            message: `Project "${newProject.prompt.substring(0, 50)}..." has been created successfully.`,
          });
          
          return newProject;
        } catch (error: any) {
          console.error('Create project error:', error);
          
          get().addNotification({
            type: 'error',
            title: 'Failed to create project',
            message: error.response?.data?.detail || 'There was an error creating your project.',
          });
          
          throw error;
        }
      },
      
      updateProject: async (projectId: string, updates: Partial<Project>) => {
        try {
          const response = await apiClient.patch(`/api/projects/${projectId}`, updates);
          const updatedProject = response.data;
          
          set({
            projects: get().projects.map(p => 
              p.project_id === projectId ? updatedProject : p
            )
          });
        } catch (error: any) {
          console.error('Update project error:', error);
          throw error;
        }
      },
      
      deleteProject: async (projectId: string) => {
        try {
          await apiClient.delete(`/api/projects/${projectId}`);
          
          set({
            projects: get().projects.filter(p => p.project_id !== projectId)
          });
          
          get().addNotification({
            type: 'success',
            title: 'Project Deleted',
            message: 'Project has been deleted successfully.',
          });
        } catch (error: any) {
          console.error('Delete project error:', error);
          
          get().addNotification({
            type: 'error',
            title: 'Failed to delete project',
            message: 'There was an error deleting your project.',
          });
          
          throw error;
        }
      },

      deleteScheduledVideo: async (channelId: string, scheduleId: string) => {
          try {
            await apiClient.delete(`/api/scheduling/channels/${channelId}/scheduled-videos/${scheduleId}`);
            
            // Remove from local state
            set({
              scheduledVideos: get().scheduledVideos.filter(v => v.schedule_id !== scheduleId)
            });
            
            get().addNotification({
              type: 'success',
              title: 'Video deleted',
              message: 'Scheduled video has been deleted successfully.',
            });
            
          } catch (error: any) {
            console.error('Delete scheduled video error:', error);
            
            get().addNotification({
              type: 'error',
              title: 'Failed to delete video',
              message: error.response?.data?.detail || 'There was an error deleting the video.',
            });
            
            throw error;
          }
        },

        // Add update video status method
        updateScheduledVideoStatus: async (channelId: string, scheduleId: string, status: string) => {
          try {
            await apiClient.put(`/api/scheduling/channels/${channelId}/scheduled-videos/${scheduleId}/status`, {
              status
            });
            
            // Update local state
            set({
              scheduledVideos: get().scheduledVideos.map(v => 
                v.schedule_id === scheduleId 
                  ? { ...v, status: status as any, updated_at: new Date().toISOString() }
                  : v
              )
            });
            
            get().addNotification({
              type: 'success',
              title: 'Status updated',
              message: `Video status changed to ${status}.`,
            });
            
          } catch (error: any) {
            console.error('Update video status error:', error);
            
            get().addNotification({
              type: 'error',
              title: 'Failed to update status',
              message: error.response?.data?.detail || 'There was an error updating the video status.',
            });
            
            throw error;
          }
        },      
      // Scheduling Actions
      createScheduleSettings: async (channelId: string, settings: any) => {
          try {
            set({ loading: { ...get().loading, scheduling: true }, errors: { ...get().errors, scheduling: null } });
            
            const response = await apiClient.post(`/api/scheduling/channels/${channelId}/settings`, {
              ...settings,
              enabled: true // Enable scheduling by default when creating
            });
            
            set({ 
              scheduleSettings: { 
                ...get().scheduleSettings, 
                [channelId]: response.data 
              } 
            });
            
            get().addNotification({
              type: 'success',
              title: 'Schedule settings created',
              message: 'Your scheduling preferences have been saved successfully.',
            });
            
          } catch (error: any) {
            console.error('Create schedule settings error:', error);
            
            get().addNotification({
              type: 'error',
              title: 'Failed to create schedule settings',
              message: error.response?.data?.detail || 'There was an error saving your settings.',
            });
            
            throw error;
          } finally {
            set({ loading: { ...get().loading, scheduling: false } });
          }
        },
      
      updateScheduleSettings: async (channelId: string, settings: any) => {
        try {
          const response = await apiClient.put(`/api/scheduling/channels/${channelId}/settings`, settings);
          
          set({
            scheduleSettings: {
              ...get().scheduleSettings,
              [channelId]: response.data
            }
          });
          
          get().addNotification({
            type: 'success',
            title: 'Schedule Updated',
            message: 'Your scheduling preferences have been updated successfully.',
          });
        } catch (error: any) {
          console.error('Update schedule settings error:', error);
          
          get().addNotification({
            type: 'error',
            title: 'Failed to update schedule',
            message: error.response?.data?.detail || 'There was an error updating your schedule.',
          });
          
          throw error;
        }
      },
      
      generateSchedule: async (channelId: string) => {
        try {
          await apiClient.post(`/api/scheduling/channels/${channelId}/generate-schedule`);
          
          // Refresh scheduled videos
          await get().fetchScheduledVideos(channelId);
          
          get().addNotification({
            type: 'success',
            title: 'Schedule Generated',
            message: 'New content schedule has been generated for your channel.',
          });
        } catch (error: any) {
          console.error('Generate schedule error:', error);
          
          get().addNotification({
            type: 'error',
            title: 'Failed to generate schedule',
            message: error.response?.data?.detail || 'There was an error generating your schedule.',
          });
          
          throw error;
        }
      },
      
      // Notification Actions
      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          timestamp: new Date(),
          read: false,
        };
        
        set({ 
          notifications: [newNotification, ...get().notifications.slice(0, 49)] // Keep max 50 notifications
        });
      },
      
      markNotificationRead: (id: string) => {
        set({
          notifications: get().notifications.map(n => 
            n.id === id ? { ...n, read: true } : n
          )
        });
      },
      
      clearNotifications: () => {
        set({ notifications: [] });
      },
      
      // Utility Actions
      clearErrors: () => {
        set({
          errors: {
            projects: null,
            channels: null,
            scheduling: null,
            subscription: null,
          }
        });
      },
    }),
    {
      name: 'tubeops-app',
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        currentTenant: state.currentTenant,
        tenantTheme: state.tenantTheme,
        selectedChannel: state.selectedChannel,
        channels: state.channels,
      }),
    }
  )
);

// Extension utilities for notification management
export const useAppStoreExtensions = {
  markNotificationAsRead: (notificationId: string) => {
    useAppStore.setState(state => ({
      notifications: state.notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    }));
  },

  clearAllNotifications: () => {
    useAppStore.setState({ notifications: [] });
  },

  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false
    };
    
    useAppStore.setState(state => ({
      notifications: [newNotification, ...state.notifications].slice(0, 50)
    }));
  }
};